/**
 * DocumentUpload — orchestrates the document-upload phase (docs_start state).
 *
 * Layout:
 *   Mobile  : stacked — collapsible AI summary → upload panels → submit
 *   Desktop : two-column [AI summary | upload panels → submit]
 *
 * Sub-components handle all presentation:
 *   ServiceEstimateCard  — estimated fee + expected TAT
 *   RequiredDocsPanel    — required-doc slots + previously uploaded list
 *   AdditionalDocsPanel  — free-form extra file uploads
 */
import * as React from 'react';
import { Send, ChevronDown, ChevronUp, Loader2, AlertCircle } from 'lucide-react';
import { toast } from '@finmin/ui';
import { useDocumentsForRequest, useUploadDocument, useDownloadDocument } from '@/api/documents';
import { useSubmitDocumentsPhase } from '@/api/service-requests';
import type { CustomerServiceRequestResponse } from '@/api/service-requests';
import { paymentGateMessage } from '@/api/payments';
import { parseAiResponse, normaliseRequiredDocs, toUploadTarget, triggerBlobDownload, formatFeeRange, formatTat } from '../utils';
import { INTERNAL_DOC_TYPES } from '../constants';
import { PALETTE_FOREST, PALETTE_AMBER, PALETTE_MINT } from '../tokens';
import type { SlotState } from './doc-upload-slot';
import { AiSummaryPanel } from './ai-summary';
import { ServiceEstimateCard } from './service-estimate-card';
import { RequiredDocsPanel } from './required-docs-panel';
import { AdditionalDocsPanel } from './additional-docs-panel';

// ── Types ─────────────────────────────────────────────────────────────────────

interface StagedFile {
  id:           string;
  documentType: string;
  file:         File;
  state:        SlotState;
  progress:     number;
  /** User-supplied display name — required for free-form 'other' docs, unused for required slots. */
  label?:       string;
}

interface Props {
  request:   CustomerServiceRequestResponse;
  onSuccess: () => void;
  /** Service Closure: uploads + submit are rejected (409) while the service is
   *  on hold or closed, so the controls are disabled instead. */
  readOnly?: boolean;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function DocumentUpload({ request, onSuccess, readOnly = false }: Props) {
  const [stagedFiles, setStagedFiles] = React.useState<StagedFile[]>([]);
  const [summaryOpen, setSummaryOpen] = React.useState(false);
  const [submitting,  setSubmitting]  = React.useState(false);

  const requestId = request.id;

  const { data: uploadedDocs = [], refetch: refetchDocs }                              = useDocumentsForRequest(requestId);
  const { mutateAsync: uploadFile }                                                     = useUploadDocument();
  const { mutateAsync: submitPhase }                                                    = useSubmitDocumentsPhase();
  const { mutateAsync: downloadDoc, isPending: isDownloading, variables: dlVars }   = useDownloadDocument();

  const parsedAi = React.useMemo(
    () => parseAiResponse(request.ai_agent1_response),
    [request.ai_agent1_response],
  );

  const requiredDocs = React.useMemo(
    () => normaliseRequiredDocs(
      request.required_documents_from_customer_predicted_by_agent1 ?? request.merged_required_documents,
      INTERNAL_DOC_TYPES,
    ),
    [request.required_documents_from_customer_predicted_by_agent1, request.merged_required_documents],
  );

  const feeDisplay = React.useMemo(
    () => formatFeeRange(request.customer_service_fee_min, request.customer_service_fee_max),
    [request.customer_service_fee_min, request.customer_service_fee_max],
  );

  const tatDisplay = React.useMemo(
    () => formatTat(request.expected_tat, request.tat_min, request.tat_max),
    [request.expected_tat, request.tat_min, request.tat_max],
  );

  async function handleDownload(documentId: string) {
    try {
      const { blob, filename } = await downloadDoc({ serviceRequestId: requestId, documentId });
      triggerBlobDownload(blob, filename);
    } catch {
      toast.error('Could not download the document. Please try again.');
    }
  }

  function stageFile(file: File, documentType: string) {
    setStagedFiles(prev => {
      const filtered = documentType !== 'other'
        ? prev.filter(f => f.documentType !== documentType)
        : prev;
      return [...filtered, {
        id: crypto.randomUUID(),
        documentType,
        file,
        state: 'staged' as SlotState,
        progress: 0,
        // 'other' docs need a user-supplied name; start empty so the field reads as required.
        label: documentType === 'other' ? '' : undefined,
      }];
    });
  }

  function removeStaged(id: string) {
    setStagedFiles(prev => prev.filter(f => f.id !== id));
  }

  function updateStagedLabel(id: string, label: string) {
    setStagedFiles(prev => prev.map(f => f.id === id ? { ...f, label } : f));
  }

  function removeStagedByType(documentType: string) {
    const staged = stagedFiles.find(f => f.documentType === documentType);
    if (staged) removeStaged(staged.id);
  }

  async function handleSubmit() {
    if (stagedFiles.length === 0 && uploadedDocs.length === 0) {
      toast.error('Please upload at least one document before submitting.');
      return;
    }
    if (hasUnnamedOther) {
      toast.error('Please name each additional document before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      for (const staged of stagedFiles.filter(f => f.state === 'staged')) {
        setStagedFiles(prev =>
          prev.map(f => f.id === staged.id ? { ...f, state: 'uploading', progress: 0 } : f),
        );
        try {
          const slot   = requiredDocs.find(d => d.document_type === staged.documentType);
          const target = slot ? toUploadTarget(slot.document_type, slot.label, staged.label) : null;
          await uploadFile({
            serviceRequestId: requestId,
            file:             staged.file,
            documentType:     target?.documentType  ?? staged.documentType,
            documentLabel:    target?.documentLabel ?? staged.label,
            onProgress:       p => setStagedFiles(prev =>
              prev.map(f => f.id === staged.id ? { ...f, progress: p } : f),
            ),
          });
          setStagedFiles(prev =>
            prev.map(f => f.id === staged.id ? { ...f, state: 'uploaded', progress: 100 } : f),
          );
        } catch (err) {
          setStagedFiles(prev =>
            prev.map(f => f.id === staged.id ? { ...f, state: 'error' } : f),
          );
          toast.error(paymentGateMessage(err) ?? `Failed to upload ${staged.file.name}. Please try again.`);
          setSubmitting(false);
          return;
        }
      }
      await submitPhase(requestId);
      toast.success('Documents submitted! Your expert will review them shortly.');
      await refetchDocs();
      onSuccess();
    } catch (err) {
      toast.error(paymentGateMessage(err) ?? (err instanceof Error ? err.message : 'Submission failed. Please check your connection and try again.'));
    } finally {
      setSubmitting(false);
    }
  }

  const anyUploading = stagedFiles.some(f => f.state === 'uploading');
  const stagedOther             = stagedFiles.filter(f => f.documentType === 'other');
  const hasUnnamedOther         = stagedOther.some(f => !f.label?.trim());

  return (
    <div className="w-full max-w-6xl mx-auto px-4 py-6">

      {/* Mobile: collapsible AI summary */}
      {parsedAi.sections.length > 0 && (
        <div className="lg:hidden mb-4 rounded-2xl border border-border bg-background overflow-hidden">
          <button
            type="button"
            onClick={() => setSummaryOpen(v => !v)}
            className="w-full flex items-center justify-between px-4 py-3 text-left"
          >
            <span className="text-sm font-semibold text-foreground">AI Financial Summary</span>
            {summaryOpen
              ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
              : <ChevronDown className="h-4 w-4 text-muted-foreground" />
            }
          </button>
          {summaryOpen && (
            <div className="border-t border-border px-4 pb-4 pt-2 max-h-72 overflow-y-auto">
              <AiSummaryPanel sections={parsedAi.sections} compact />
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6 lg:gap-8 items-start">

        {/* Desktop: AI summary panel */}
        <div className="hidden lg:block">
          {parsedAi.sections.length > 0 ? (
            <div className="rounded-2xl border border-border bg-background p-6">
              <h3 className="font-semibold text-base text-foreground mb-4">AI Financial Summary</h3>
              <AiSummaryPanel sections={parsedAi.sections} />
            </div>
          ) : (
            <div
              className="rounded-2xl p-8 flex flex-col items-center justify-center text-center gap-3 h-full"
              style={{ backgroundColor: PALETTE_MINT }}
            >
              <Loader2 className="h-8 w-8 animate-spin" style={{ color: PALETTE_FOREST }} />
              <p className="text-sm text-muted-foreground">AI analysis is being prepared…</p>
            </div>
          )}
        </div>

        {/* Right column: upload panels */}
        <div className="space-y-4">
          <ServiceEstimateCard feeDisplay={feeDisplay} tatDisplay={tatDisplay} />

          <div
            className={readOnly ? 'pointer-events-none opacity-60' : undefined}
            aria-disabled={readOnly || undefined}
          >
          <RequiredDocsPanel
            requiredDocs={requiredDocs}
            stagedFiles={stagedFiles}
            uploadedDocs={uploadedDocs}
            isDownloading={isDownloading}
            downloadingDocId={dlVars?.documentId}
            onFileSelected={stageFile}
            onRemoveStaged={removeStagedByType}
            onDownload={handleDownload}
          />

          <AdditionalDocsPanel
            stagedOther={stagedOther}
            onStageFile={stageFile}
            onRemove={removeStaged}
            onLabelChange={updateStagedLabel}
          />
          </div>

          {readOnly && (
            <div className="flex gap-2 rounded-xl border border-border bg-muted/40 px-3 py-2.5">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">
                Document uploads are paused while this service is on hold or closed.
              </p>
            </div>
          )}

          {!readOnly && stagedFiles.length === 0 && (
            <div className="flex gap-2 rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800/40 px-3 py-2.5">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
              <p className="text-xs text-amber-800 dark:text-amber-300">
                Please upload at least one document to submit your service request.
              </p>
            </div>
          )}

          {stagedFiles.length > 0 && hasUnnamedOther && (
            <div className="flex gap-2 rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800/40 px-3 py-2.5">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
              <p className="text-xs text-amber-800 dark:text-amber-300">
                Please give each additional document a name before submitting.
              </p>
            </div>
          )}

          <button
            type="button"
            onClick={handleSubmit}
            disabled={readOnly || submitting || anyUploading || stagedFiles.length === 0 || hasUnnamedOther}
            className="w-full h-12 rounded-2xl flex items-center justify-center gap-2 text-sm font-bold text-white transition-all disabled:opacity-50 active:scale-[0.99]"
            style={{ backgroundColor: submitting ? 'hsl(var(--muted))' : PALETTE_AMBER }}
          >
            {submitting
              ? <><Loader2 className="h-4 w-4 animate-spin" />Submitting…</>
              : <><Send className="h-4 w-4" />Submit Documents</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}
